import { Injectable, inject } from '@angular/core';
import { type CanActivate, Router, type UrlTree } from '@angular/router';

import { AuthService } from '../_services/auth.service';

/**
 * The Angular app is RMBF's officer console (base TDD §3 / TDD 06 revision) - only the 4 leadership
 * positions (Chairman, Secretary, Treasurer, Charter Chairman), all sharing the single ROLE_ClientAdmin
 * role, should be able to use it at all. Every other member uses the Flutter mobile app instead.
 *
 * The one other account let in is ApplicationSupport (September 2026): the support login, not a
 * Member, used in production to download logs and adjust translations. It reaches only those two
 * screens - every chapter route is `roles: ['ClientAdmin']`, and the menu hides the rest.
 *
 * This is deliberately separate from AuthGuard's per-route `roles` data. Several routes under
 * FullComponent (dashboard, notifications, change-password, members list, events list) use
 * `roles: []` - those arrays were never meant to be the thing keeping ordinary members out. This
 * guard is the single, blanket enforcement point for that instead: applied once, on the top-level
 * route wrapping FullComponent, alongside AuthGuard. Since ApplicationSupport also passes it, a new
 * chapter route must say `roles: ['ClientAdmin']`; only screens that work for any login (change
 * password, notifications) stay `roles: []`.
 *
 * Note (August 2026): the login endpoint itself is erp-base-owned, shared with livestock/sgb, and has
 * no reliable per-request web-vs-mobile signal to gate on at authentication time (see TDD 06 revision
 * note) - so a non-admin member can still technically obtain a valid JWT via this app's login form.
 * This guard is what actually stops them from getting anywhere with it once they do: it fires
 * immediately after login and redirects to /web-console-restricted before any admin-only data loads.
 * Every ClientAdmin-only action is separately enforced server-side via @PreAuthorize regardless.
 */
@Injectable({ providedIn: 'root' })
export class WebConsoleAccessGuard implements CanActivate {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(): boolean | UrlTree {
    if (!this.authService.isLoggedInSync()) {
      return true; // AuthGuard (runs alongside this guard) owns the redirect-to-login for this case
    }
    if (this.authService.hasAnyRole(['ClientAdmin', 'ApplicationSupport'])) {
      return true;
    }
    return this.router.createUrlTree(['/web-console-restricted']);
  }
}
